<template>
  <ConfigPage :max-width="1000">
    <ConfigItem
      :title="__('Templates de formulários')"
      :description="
        __(
          'Define os templates que a plataforma disponibilizará para as unidades.'
        )
      "
    >
      <template #action>
        <GlossaryMenu
          :glossary="glossary"
          title="Variáveis disponíveis"
          text="Você pode utilizar variáveis dinâmicas para inserir automaticamente informações do processo no texto.<br>
Essas variáveis serão substituídas pelos valores reais no momento da geração.<br>

Utilize o seguinte formato: <code>${nome_da_variavel}</code><br><br>

Certifique-se de utilizar exatamente o nome das variáveis disponíveis."
        />
      </template>

      <div class="d-flex justify-end mb-4">
        <v-btn color="primary" @click="openForm">
          <v-icon left>mdi-plus</v-icon>
          {{ __("Adicionar template") }}
        </v-btn>
      </div>

      <v-skeleton-loader v-if="loading" type="table" />

      <v-card outlined class="mb-12" v-else>
        <v-simple-table>
          <thead>
            <tr>
              <th>{{ __("Tipo") }}</th>
              <th>{{ __("Subtipo") }}</th>
              <th>{{ __("Template") }}</th>
              <th class="text-right"></th>
            </tr>
          </thead>

          <tbody>
            <tr v-for="item in items" :key="item.id">
              <td>{{ item.lead_type.description }}</td>
              <td>{{ item.subtype_description }}</td>
              <td>{{ item.name }}</td>
              <td class="text-right">
                <v-btn icon color="primary" @click="viewTemplate(item)">
                  <v-icon>mdi-magnify</v-icon>
                </v-btn>

                <v-btn icon color="orange" @click="openFormFilled(item)">
                  <v-icon>mdi-pencil</v-icon>
                </v-btn>

                <v-btn icon color="red" @click="handleDelete(item)">
                  <v-icon>mdi-delete</v-icon>
                </v-btn>
              </td>
            </tr>

            <tr v-if="!items.length">
              <td colspan="4" class="text-center py-4">
                {{ __("Nenhum template cadastrado") }}
              </td>
            </tr>
          </tbody>
        </v-simple-table>
      </v-card>
    </ConfigItem>

    <FormTemplateForm ref="form" @submit="handleSubmit" />
    <ConfirmationDialog ref="confirmDialog" />

    <FileDialog
      v-model="preview"
      :attachment="selectedPreviewFile"
      :no-edit="true"
      :no-delete="true"
    />
  </ConfigPage>
</template>

<script>
import ConfigPage from "@/layouts/configs/ConfigPage";
import ConfigItem from "@/layouts/configs/ConfigItem";
import FormTemplateForm from "./FormTemplateForm";
import ConfirmationDialog from "@/components/tools/ConfirmationDialog";
import FileDialog from "@/components/FileDialog.vue";
import ToastsMixin from "@/mixins/ToastsMixin.vue";
import i18n from "@/mixins/i18n";
import * as formTemplatesService from "@/services/forms/form_templates";
import { showGlossary } from "@/services/forms/form_templates";
import GlossaryMenu from "@/components/Glossary/GlossaryMenu.vue";

export default {
  mixins: [i18n, ToastsMixin],

  components: {
    ConfigPage,
    ConfigItem,
    FormTemplateForm,
    ConfirmationDialog,
    FileDialog,
    GlossaryMenu,
  },

  data() {
    return {
      items: [],
      loading: false,
      preview: false,
      selectedPreviewFile: null,
      glossary: [],
    };
  },

  mounted() {
    this.fetchTemplates();
    this.fetchGlossary();
  },

  methods: {
    async fetchGlossary() {
      this.glossary = await showGlossary();
      console.log(this.glossary);
    },

    async fetchTemplates() {
      try {
        this.loading = true;
        const response = await formTemplatesService.list();
        this.items = response.data || response;
      } catch (e) {
        this.toastError(
          e.response?.data?.message ||
            this.__("Erro ao carregar templates", "form_templates")
        );
      } finally {
        this.loading = false;
      }
    },

    openForm() {
      this.$refs.form.open();
    },

    openFormFilled(item) {
      this.$refs.form.open(item);
    },

    viewTemplate(item) {
      const url = item.public_url || item.file_url;

      if (!url) {
        this.toast(this.__("Template sem arquivo", "form_templates"));
        return;
      }

      this.selectedPreviewFile = {
        id: item.id,
        name: item.name,
        description: item.name,
        extension: this.getTemplateExtension(item),
        public_url: url,
        category_id: null,
      };

      this.preview = true;
    },

    getTemplateExtension(item) {
      if (item.extension) {
        return item.extension.toLowerCase();
      }

      const url = item.public_url || item.file_url || "";
      const cleanUrl = url.split("?")[0];
      const parts = cleanUrl.split(".");

      return parts.length > 1 ? parts.pop().toLowerCase() : "";
    },

    async handleDelete(item) {
      const title = this.__("Deseja remover este template?", "form_templates");
      const description = this.__(
        "Esta ação não poderá ser desfeita",
        "form_templates"
      );

      this.$refs.confirmDialog.confirm(
        title,
        async ({ close, startLoading, stopLoading }) => {
          startLoading();
          try {
            await formTemplatesService.remove(item.id);
            this.items = this.items.filter((i) => i.id !== item.id);
            this.toastSuccess(
              this.__("Template removido com sucesso", "form_templates")
            );
            close();
          } catch (e) {
            console.error(e);
            this.toastError(
              e.response?.message ||
                this.__("Erro ao remover", "form_templates")
            );
          } finally {
            stopLoading();
          }
        },
        null,
        {
          description,
          confirmText: this.__("Excluir", "form_templates"),
          cancelText: this.__("Cancelar", "form_templates"),
          color: "error",
        }
      );
    },

    async handleSubmit() {
      await this.fetchTemplates();
    },
  },
};
</script>
